// Számlázási profilok lista (billing, BELSŐ nézet) — a KÖZÖS DataTable-re épül (az invoices a
// referencia-minta): szerver-oldali (BFF) kereső (300 ms debounce; név/adószám/város/cím/ügyvezető) + ügyfél-
// és „csak elsődleges" szűrő + darabszám-meta a toolbarban, szerver-rendezés (billingName / clientName /
// taxNumber), végtelen görgetés (100/lap). A /billing-profiles BFF-végpontról fogyaszt (busz-sorok + ügyfél-név
// feloldva; tenant-scope grant-fal RLS Odin-oldalon). Page-gate: `billing_profiles.view`.
//
// Írás-vertikum: create/edit FormSheet (billing_profiles.manage) + elsődlegessé tétel (set-primary action,
// optimista) + törlés (billing_profiles.delete; az elsődleges profilnál a BFF 409 → toast + rollback).
import { type BillingProfileRowSchema } from '@atlas/contracts';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { BillingProfileFormSheet } from '@/components/billing-profiles/billing-profile-form-sheet';
import { BillingProfileRowActions } from '@/components/billing-profiles/billing-profile-row-actions';
import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DetailsExpander, type DetailsRow } from '@/components/shared/details-expander';
import { EmptyState } from '@/components/shared/empty-state';
import { LabelChip } from '@/components/shared/label-chip';
import { ListErrorState } from '@/components/shared/list-error-state';
import { ListNewButton } from '@/components/shared/list-new-button';
import { ListToolbar } from '@/components/shared/list-toolbar';
import { Text } from '@/components/ui/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { billingProfileKeys } from '@/features/billing-profiles/api';
import { copyBillingDetails } from '@/features/billing-profiles/billing-details-text';
import {
  formatDirectorPeriod,
  overlappingDirectorIndexes,
  sortDirectorsForDisplay,
} from '@/features/billing-profiles/directors';
import { useListFilters } from '@/hooks/use-list-filters';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { isForbiddenStatus } from '@/lib/error-status';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

// A SZŰRŐK search-paramok (a state-konvenció: a szűrt nézet legyen linkelhető és F5-biztos) — a route-nak
// ismernie kell a kulcsokat, különben a router kiszűri őket. A default-értékű kulcs nem kerül az URL-be.
const billingProfilesSearchSchema = z.object({
  q: z.string().optional(),
  primary: z.string().optional(),
  sort: z.string().optional(),
  order: z.string().optional(),
});

export const Route = createFileRoute('/billing-profiles')({
  validateSearch: (search) => billingProfilesSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: BillingProfilesPage,
});

type BillingProfileRow = z.infer<typeof BillingProfileRowSchema>;
type ListPage = Awaited<ReturnType<typeof api.billingProfiles.query>>;

const PAGE_SIZE = 100;
const ALL = '__all__';
const ONLY_PRIMARY = 'primary';

/** Országkód → lokalizált (magyar) országnév — a cím-oszlop ország-sorához (mint az accounts-listán).
 *  `Intl.DisplayNames` böngésző-natív; ismeretlen/hibás kód → maga a kód (fallback). */
const countryDisplay = new Intl.DisplayNames(['hu'], { type: 'region' });
function countryLabel(code: string | null): string | null {
  if (!code) return null;
  try {
    return countryDisplay.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/**
 * Az ügyvezető-történet „Részletek" sorai: `NÉV: időszak`, a legfrissebb elöl. Az ÁTFEDŐ bejegyzések
 * figyelmeztető ikont kapnak — ugyanaz a szabály, amit a szerkesztő is jelez (`directors.ts`), hogy a
 * két felület ne mondjon mást ugyanarról az adatról.
 *
 * A `key` azért kell, mert a CÍMKE itt a név — egy visszatérő ügyvezető kétszer is szerepelhet a listában.
 */
export function directorDetailRows(
  p: BillingProfileRow,
  language: string,
  t: TFunction,
): DetailsRow[] {
  const sorted = sortDirectorsForDisplay(p.managingDirectors);
  const overlapping = overlappingDirectorIndexes(sorted);
  return sorted.map((d, i) => ({
    key: `${d.name}-${d.startsOn ?? ''}-${d.endsOn ?? ''}-${String(i)}`,
    label: d.name,
    value: (
      <span className="inline-flex min-w-0 items-center gap-1">
        <span className="min-w-0 truncate">{formatDirectorPeriod(d, language, t)}</span>
        {overlapping.has(i) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                aria-label={t('billingProfiles.directors.overlapWarning')}
                className="text-warning-500 inline-flex shrink-0"
              >
                <ExclamationTriangleIcon className="size-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{t('billingProfiles.directors.overlapWarning')}</TooltipContent>
          </Tooltip>
        )}
      </span>
    ),
  }));
}

function BillingProfilesPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('billing_profiles.view');
  const canManage = can('billing_profiles.manage');
  const canDelete = can('billing_profiles.delete');

  // A SZŰRŐK az URL-ben (linkelhető + F5-biztos nézet); a defaultok egyben a kulcs-készlet.
  const { filters: urlFilters, setFilters } = useListFilters({
    q: '',
    primary: ALL,
    sort: 'billingName',
    order: 'asc',
  });
  const q = urlFilters.q;
  const primaryFilter = urlFilters.primary;
  const sort = urlFilters.sort;
  const order = urlFilters.order as 'asc' | 'desc';
  // A beviteli mező AZONNALI (lokális) állapota — az URL-be a debounce-olt érték kerül.
  const [searchInput, setSearchInput] = useState(q);

  const [editTarget, setEditTarget] = useState<BillingProfileRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BillingProfileRow | null>(null);

  // Kereső-debounce (300 ms).
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === q) return;
    const timer = setTimeout(() => setFilters({ q: trimmed }), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, setFilters]);

  // Page-gate: billing_profiles.view nélkül meg sem kérdezzük a szervert.
  const gated = !permsLoading && !canView;

  const onlyPrimary = primaryFilter === ONLY_PRIMARY;
  const filters = { q, onlyPrimary, sort, order };
  const listKey = billingProfileKeys.list(filters);

  const infinite = useInfiniteQuery({
    queryKey: listKey,
    enabled: !gated,
    queryFn: ({ pageParam }) =>
      api.billingProfiles.query({
        query: {
          q: q || undefined,
          onlyPrimary: onlyPrimary ? true : undefined,
          sort,
          order,
          limit: PAGE_SIZE,
          offset: pageParam,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.status === 200 ? (lastPage.body.nextOffset ?? undefined) : undefined,
  });

  const pages = useMemo(() => infinite.data?.pages ?? [], [infinite.data]);
  const rows = useMemo(
    () => pages.flatMap((p) => (p.status === 200 ? p.body.profiles : [])),
    [pages],
  );
  const okPage = pages.find((p) => p.status === 200);
  const total = okPage?.status === 200 ? okPage.body.total : 0;
  const hasNoSuccess = pages.length > 0 && !pages.some((p) => p.status === 200);
  const isError = gated || infinite.isError || hasNoSuccess;
  const isForbidden = gated || pages.some((p) => isForbiddenStatus(p.status));
  const hasFilters = q !== '' || primaryFilter !== ALL;

  // Oszlop-fejléc kattintás: ugyanaz az oszlop → irány-váltás; MÁS oszlop → az új oszlop, `asc`-ből.
  const toggleSort = useCallback(
    (col: string) => {
      setFilters(
        col === sort ? { order: order === 'asc' ? 'desc' : 'asc' } : { sort: col, order: 'asc' },
      );
    },
    [sort, order, setFilters],
  );
  const { fetchNextPage } = infinite;
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  // Törlés (optimista lista-sor eltávolítás + rollback): a soft-delete a BFF `/billing-profiles/:id`. Az
  // elsődleges profilt a BFF 409-cel tiltja → a hook onError-ja rollbackel + a conflict-toastot mutatja.
  // A TÖRLÉS IS OCC-s: a szerver a sor verzióját várja `If-Match`-ben. Enélkül 428 — és nem véletlenül: a
  // szerver-oldali guardok (elsődleges profil · használatban lévő kártya · számlázott hozzárendelés) az
  // OLVASOTT állapotra szólnak, a törlés viszont később megy; a kettő között beérkező módosítás enélkül
  // elveszett volna.
  const removeMutation = useEntityMutation<{ id: string; version: number }, InfiniteData<ListPage>>(
    {
      mutationFn: ({ id, version }) =>
        api.billingProfileDelete.mutation({
          params: { id },
          headers: { 'if-match': String(version) },
        }),
      invalidateKeys: [billingProfileKeys.lists()],
      optimistic: {
        queryKey: listKey,
        update: (old) => {
          const id = deleteTarget?.id;
          if (!old || !id) return old;
          return {
            ...old,
            pages: old.pages.map((p) =>
              p.status === 200
                ? {
                    ...p,
                    body: {
                      ...p.body,
                      profiles: p.body.profiles.filter((pr) => pr.id !== id),
                      total: Math.max(0, p.body.total - 1),
                    },
                  }
                : p,
            ),
          };
        },
      },
      messages: {
        success: t('toast:billingProfiles.deleted'),
        conflict: t('toast:billingProfiles.deletePrimary'),
        forbidden: t('toast:billingProfiles.forbidden'),
        validation: t('toast:billingProfiles.validation'),
        error: t('toast:billingProfiles.error'),
      },
    },
  );

  // Elsődlegessé tétel (optimista: ez a sor primary, a többi nem) — a BFF a set-primary action-nel oldja fel a
  // régi elsődlegest. If-Match a sor version-jéből (428 hiányában).
  const setPrimaryMutation = useEntityMutation<
    { id: string; version: number },
    InfiniteData<ListPage>
  >({
    mutationFn: ({ id, version }) =>
      api.billingProfileSetPrimary.mutation({
        params: { id },
        headers: { 'if-match': String(version) },
      }),
    invalidateKeys: [billingProfileKeys.lists()],
    optimistic: {
      queryKey: listKey,
      update: (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((p) =>
            p.status === 200
              ? {
                  ...p,
                  body: {
                    ...p.body,
                    profiles: p.body.profiles.map((pr) => ({
                      ...pr,
                      isPrimary:
                        pr.id === setPrimaryTargetId.current
                          ? true
                          : pr.clientId === setPrimaryTargetClientId.current
                            ? false
                            : pr.isPrimary,
                    })),
                  },
                }
              : p,
          ),
        };
      },
    },
    messages: {
      success: t('toast:billingProfiles.primarySet'),
      conflict: t('toast:billingProfiles.conflict'),
      forbidden: t('toast:billingProfiles.forbidden'),
      validation: t('toast:billingProfiles.validation'),
      error: t('toast:billingProfiles.error'),
    },
  });

  // A set-primary optimista updaterének kell a cél-id + cél-ügyfél (a többi elsődleges feloldásához) — ref, hogy
  // a closure mindig a legutóbbi értéket lássa (a mutate előtt beállítva).
  const setPrimaryTargetId = useRef('');
  const setPrimaryTargetClientId = useRef('');

  function handleSetPrimary(row: BillingProfileRow): void {
    setPrimaryTargetId.current = row.id;
    setPrimaryTargetClientId.current = row.clientId;
    setPrimaryMutation.mutate({ id: row.id, version: row.version });
  }

  function confirmDelete(): void {
    if (!deleteTarget) return;
    removeMutation.mutate(
      { id: deleteTarget.id, version: deleteTarget.version },
      { onSettled: () => setDeleteTarget(null) },
    );
  }

  const emDash = <span className="text-muted-foreground">—</span>;

  const columns: DataTableColumn<BillingProfileRow>[] = [
    {
      // 1. Számlázási név (rendezhető, itemTitle) — a fő sor. A legtöbb ügyfélnek egy (elsődleges) profilja van,
      //    ezért CSAK a NEM-elsődlegeset jelöljük egy halvány ikonnal + tooltippel (a „kivételt" mutatjuk, mint a
      //    jogosultságoknál). Alatta halvány ügyvezető, ha van.
      id: 'billingName',
      header: t('billingProfiles.columns.billingName'),
      sortable: true,
      cardLabel: t('billingProfiles.columns.billingName'),
      width: 'minmax(0,2fr)',
      multiline: true,
      // MOBIL-kártya: az ügyvezető-párok a kártya EGYETLEN „Részletek" rácsába (a kártyán a kibontót a
      // kártya adja — a desktop-kibontó ott nem renderel; lásd a `DetailsExpander` fejlécét).
      cardExtraItems: (p) =>
        directorDetailRows(p, i18n.language, t).map((r) => ({
          label: r.label,
          value: r.value,
          ...(r.key !== undefined ? { key: r.key } : {}),
        })),
      cell: (p) => (
        <div className="min-w-0 space-y-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <Text variant="itemTitle" as="span" className="min-w-0 truncate">
              {p.billingName?.trim() ? p.billingName : emDash}
            </Text>
            {!p.isPrimary && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    aria-label={t('billingProfiles.notPrimaryTooltip')}
                    className="text-muted-foreground/70 inline-flex shrink-0"
                  >
                    <InformationCircleIcon className="size-4" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>{t('billingProfiles.notPrimaryTooltip')}</TooltipContent>
              </Tooltip>
            )}
          </div>
          {/* Az ÜGYVEZETŐ(K) a „Részletek" alá kerültek (user-döntés): a történet több sor is lehet, ami a
              cím alatti halvány egysorosba nem fér — és a többi lista-oldal is így tagolja a másodlagos
              adatot. Ügyvezető nélkül a kibontó nem is jelenik meg (nincs üres panel). */}
          <DetailsExpander rows={directorDetailRows(p, i18n.language, t)} />
        </div>
      ),
    },
    {
      // 2. Cím — az accounts-lista 2. oszlopának azonos design-ja: muted, „irsz város, utca" egy sorban,
      //    alatta az ország `tone="tertiary"` külön sorban (a countryCode → lokalizált név, mint az accountsnál).
      id: 'address',
      header: t('billingProfiles.columns.address'),
      cardLabel: t('billingProfiles.columns.address'),
      width: 'minmax(0,1.75fr)',
      className: 'text-muted-foreground',
      multiline: true,
      cell: (p) => {
        const cityLine = [p.zipCode, p.city].filter(Boolean).join(' ');
        const line1 = [cityLine, p.address].filter(Boolean).join(', ');
        const country = countryLabel(p.countryCode);
        if (!line1 && !country) return emDash;
        return (
          <div className="min-w-0 space-y-0.5">
            {line1 && <div className="truncate">{line1}</div>}
            {country && (
              <Text tone="tertiary" as="div" className="truncate">
                {country}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      // 3. Adószám — HU/EU chip az adószám ELŐTT (az ügyfél-lista „Számlázási adatok" oszlopának mintája):
      //    countryCode-chip + adószám, majd „EU"-chip + EU-adószám (halványabb). Rendezés: taxNumber.
      id: 'taxNumber',
      header: t('billingProfiles.columns.taxNumber'),
      sortable: true,
      cardLabel: t('billingProfiles.columns.taxNumber'),
      width: 'minmax(0,1.5fr)',
      multiline: true,
      cell: (p) => {
        if (!p.taxNumber && !p.euTaxNumber) return emDash;
        return (
          <div className="min-w-0 space-y-0.5">
            {p.taxNumber && (
              <div className="flex items-center gap-1 tabular-nums">
                <LabelChip label={p.countryCode ?? 'HU'} />
                <span className="truncate">{p.taxNumber}</span>
              </div>
            )}
            {p.euTaxNumber && (
              <div className="flex items-center gap-1 tabular-nums">
                <LabelChip label="EU" />
                <Text variant="secondary" as="span" className="truncate">
                  {p.euTaxNumber}
                </Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      // 4. Ügyfél (feloldott név; rendezhető) — UTOLSÓ adat-oszlop.
      id: 'clientName',
      card: 'subtitle',
      header: t('billingProfiles.columns.client'),
      sortable: true,
      cardLabel: t('billingProfiles.columns.client'),
      width: 'minmax(0,1.25fr)',
      className: 'text-muted-foreground',
      cell: (p) => (p.clientName ? <span className="truncate">{p.clientName}</span> : emDash),
    },
  ];

  return (
    <AppShell
      primaryAction={
        canManage
          ? {
              icon: <PlusIcon className="size-5 stroke-2" />,
              label: t('billingProfiles.new'),
              onClick: () => setCreateOpen(true),
            }
          : undefined
      }
      toolbar={
        <ListToolbar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: t('common.searchPlaceholder'),
          }}
          filters={[
            {
              key: 'primary',
              label: t('billingProfiles.filterPrimary'),
              value: primaryFilter,
              onChange: (v: string) => setFilters({ primary: v }),
              options: [{ value: ONLY_PRIMARY, label: t('billingProfiles.onlyPrimary') }],
              allValue: ALL,
            },
          ]}
          meta={
            infinite.isFetching && !infinite.isFetchingNextPage
              ? t('billingProfiles.loading')
              : !isError && !infinite.isLoading
                ? t('billingProfiles.countOf', { shown: rows.length, total })
                : ''
          }
          actions={
            <div className="hidden md:block">
              <ListNewButton
                label={t('billingProfiles.new')}
                onClick={() => setCreateOpen(true)}
                enabled={canManage}
                disabledTooltip={t('common.noCreatePermission')}
              />
            </div>
          }
        />
      }
    >
      <div className="flex h-full flex-col px-4 pb-2">
        <DataTable<BillingProfileRow>
          columns={columns}
          rows={rows}
          getRowId={(p) => p.id}
          sort={sort}
          order={order}
          onSort={toggleSort}
          isLoading={!gated && (permsLoading || infinite.isLoading)}
          isError={isError}
          errorState={
            <ListErrorState forbidden={isForbidden} onRetry={() => void infinite.refetch()} />
          }
          emptyState={
            hasFilters ? (
              <EmptyState
                title={t('billingProfiles.noMatches')}
                description={t('billingProfiles.noMatchesDesc')}
              />
            ) : (
              <EmptyState
                title={t('billingProfiles.empty')}
                description={t('billingProfiles.emptyDesc')}
              />
            )
          }
          hasNextPage={infinite.hasNextPage}
          isFetchingNextPage={infinite.isFetchingNextPage}
          onLoadMore={loadMore}
          rowActionsLabel={t('billingProfiles.columns.actions')}
          // ikon-gombonként ~2rem + a cella 1rem-es kerete: másolás (mindig) + elsődleges + szerkesztés + törlés
          actionsColWidth={`${String(1 + 2 * (1 + (canManage ? 2 : 0) + (canDelete ? 1 : 0)))}rem`}
          rowActions={(p) => (
            <BillingProfileRowActions
              row={p}
              canManage={canManage}
              canDelete={canDelete}
              onEdit={(row) => setEditTarget(row)}
              onSetPrimary={handleSetPrimary}
              onDelete={(row) => setDeleteTarget(row)}
              onCopy={(row) => copyBillingDetails(row, t)}
              t={t}
            />
          )}
        />
      </div>

      <BillingProfileFormSheet open={createOpen} onOpenChange={setCreateOpen} profile={null} />
      <BillingProfileFormSheet
        open={editTarget !== null}
        onOpenChange={(open) => !open && setEditTarget(null)}
        profile={editTarget}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('billingProfiles.deleteTitle')}
        description={t('billingProfiles.deleteDesc', { name: deleteTarget?.billingName ?? '' })}
        confirmLabel={t('billingProfiles.delete')}
        cancelLabel={t('billingProfiles.cancel')}
        pendingLabel={t('billingProfiles.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}
